import { useEffect, useRef, useState } from 'react';
import Plot from 'react-plotly.js';
import useApiBase from './api/useApiBase';
import AuthGate from './auth/AuthGate';
import useAuthSession from './auth/useAuthSession';
import usePageTitle from './usePageTitle';
import {
  formatSiteDateTime,
  parseUtcInstant,
  resolveDisplayTimezone,
  utcInstantToSiteWallClockDate,
} from './siteTime';

const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toDisplayValue = (value) => (value === null || value === undefined ? 'N/A' : value);
const toBoolean = (value) => value === true || value === 'true' || value === 1 || value === '1';
const toConfigDisplay = (value) => {
  if (value === null || value === undefined) {
    return 'N/A';
  }
  if (typeof value === 'object') {
    if (value.kind === 'duration' && value.display) {
      return value.display;
    }
    try {
      return JSON.stringify(value);
    } catch (err) {
      return String(value);
    }
  }
  return String(value);
};

/** Boolean from the API, preserving null/undefined as unknown state. */
const toBooleanOrNull = (value) => (typeof value === 'boolean' ? value : null);

/**
 * Coloured status light with a label.
 * `ok` true → green, false → red, null/undefined → grey (unknown / no heartbeat yet).
 * `value` is the boolean shown in the tooltip; defaults to `ok`. Pass it explicitly when the
 * light colour is inverted relative to the flag (e.g. isDowntimeActive: green = not in downtime).
 */
const StatusLight = ({ label, ok, value }) => {
  const shown = value === undefined ? ok : value;
  const color = ok === null || ok === undefined ? '#9ca3af' : ok ? '#16a34a' : '#dc2626';
  const titleState = shown === null || shown === undefined ? 'no data' : shown ? 'true' : 'false';
  return (
    <span
      title={`${label}: ${titleState}`}
      style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: '#0f172a' }}
    >
      <span
        style={{
          width: '12px',
          height: '12px',
          borderRadius: '50%',
          backgroundColor: color,
          boxShadow: `0 0 4px ${color}`,
          display: 'inline-block',
        }}
      />
      {label}
    </span>
  );
};

const vemagValuePresent = (v) => v !== null && v !== undefined && v !== '';

const DetailCard = ({ label, value }) => (
  <div
    style={{
      background: '#f8fafc',
      border: '1px solid #e2e8f0',
      borderRadius: '10px',
      padding: '12px 14px',
    }}
  >
    <div style={{ fontSize: '12px', color: '#475569', marginBottom: '4px', textTransform: 'uppercase' }}>
      {label}
    </div>
    <div style={{ fontSize: '22px', fontWeight: 700, color: '#0f172a' }}>{value}</div>
  </div>
);

/** Align pack mean on the time axis like order-chart shifted window start: t − max(0, delay − interval). */
const packMeanPlotTime = (settingTime, delaySeconds, intervalSeconds, timeZone) => {
  const instant = parseUtcInstant(settingTime);
  if (!instant) {
    return null;
  }
  const delay = toNumberOrNull(delaySeconds);
  const interval = toNumberOrNull(intervalSeconds);
  const delaySec = delay ?? 0;
  const intervalSec = interval ?? 0;
  const shiftSec = Math.max(0, delaySec - intervalSec);
  const shiftedIso = new Date(instant.getTime() - shiftSec * 1000).toISOString();
  return timeZone
    ? utcInstantToSiteWallClockDate(shiftedIso, timeZone)
    : new Date(instant.getTime() - shiftSec * 1000);
};

function LiveChartPage() {
  const [dataHistory, setDataHistory] = useState([]);
  const [error, setError] = useState('');
  const [configError, setConfigError] = useState('');
  const [configPayload, setConfigPayload] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const [lineId, setLineId] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const value = params.get('line_id');
    const parsed = value ? Number.parseInt(value, 10) : NaN;
    return Number.isNaN(parsed) ? 27 : parsed;
  });
  usePageTitle(`Live Chart – Line ${lineId}`);
  const historyRef = useRef([]);
  const asbRateHistoryRef = useRef([]);
  const [asbRateHistory, setAsbRateHistory] = useState([]);
  const [latestAsbMessagesPerSecond, setLatestAsbMessagesPerSecond] = useState(null);
  const [asbRateWindowSec, setAsbRateWindowSec] = useState(null);
  const [displayTimezone, setDisplayTimezone] = useState(null);
  const [serverName, setServerName] = useState(null);
  const [now, setNow] = useState(() => new Date());
  const MAX_HISTORY = 15000; // Keep last 10000 data points
  const apiBase = useApiBase();
  const auth = useAuthSession(apiBase);

  useEffect(() => {
    if (!auth.authChecked || !auth.isAuthenticated) {
      return;
    }

    let isMounted = true;

    const fetchLiveChart = async () => {
      try {
        const response = await auth.fetchWithAuth('/live_chart/', {
          params: { line_id: lineId },
        });
        if (!response.ok) {
          throw new Error(`Request failed: ${response.status}`);
        }

        const payload = await response.json();
        if (isMounted) {
          if (payload?.line_id != null) {
            const parsedLineId = Number.parseInt(payload.line_id, 10);
            if (!Number.isNaN(parsedLineId)) {
              setLineId(parsedLineId);
            }
          }
          const newData = payload?.data;
          if (newData) {
            if (newData.server_name) {
              setServerName(newData.server_name);
            }
            setDisplayTimezone(resolveDisplayTimezone(newData.server_name));
            const normalizedData = {
              ...newData,
              setting_time: newData.setting_time ?? null,
              new_setting: toNumberOrNull(newData.new_setting),
              accepted: toNumberOrNull(newData.accepted),
              nominal_weight: toNumberOrNull(newData.nominal_weight),
              correction: toNumberOrNull(newData.correction),
              current_ga: toNumberOrNull(newData.current_ga),
              rejections_count: toNumberOrNull(newData.rejections_count),
              recent_underweight_count: toNumberOrNull(newData.recent_underweight_count),
              mean_setting_difference: toNumberOrNull(newData.mean_setting_difference),
              current_mu: toNumberOrNull(newData.current_mu),
              pack_mean: toNumberOrNull(newData.pack_mean),
              line_delay_seconds: toNumberOrNull(newData.line_delay_seconds),
              update_setting_interval_seconds: toNumberOrNull(newData.update_setting_interval_seconds),
              order_id: newData.order_id ?? null,
              order_length: toNumberOrNull(newData.order_length),
              units_scheduler_in_use: toBoolean(newData.units_scheduler_in_use),
              min_unit_weight: toNumberOrNull(newData.min_unit_weight),
              max_unit_weight: toNumberOrNull(newData.max_unit_weight),
              units_per_pack: toNumberOrNull(newData.units_per_pack),
              current_vemag_setting_value: toNumberOrNull(newData.current_vemag_setting_value),
              current_vemag_setting_date: newData.current_vemag_setting_date ?? null,
              last_package_at: newData.last_package_at ?? null,
              last_package_weight_g: toNumberOrNull(newData.last_package_weight_g),
              max_cut_to_vemag_diff_seconds: toNumberOrNull(newData.max_cut_to_vemag_diff_seconds),
              line_heartbeat_is_running: toBooleanOrNull(newData.line_heartbeat_is_running),
              line_heartbeat_is_downtime_active: toBooleanOrNull(newData.line_heartbeat_is_downtime_active),
              line_heartbeat_is_ai_turned_on: toBooleanOrNull(newData.line_heartbeat_is_ai_turned_on),
              line_heartbeat_run_id: newData.line_heartbeat_run_id ?? null,
            };
            historyRef.current = [
              ...historyRef.current,
              normalizedData,
            ].slice(-MAX_HISTORY);
            setDataHistory([...historyRef.current]);
          }
          const asbRate = toNumberOrNull(payload?.asb_messages_per_second);
          if (asbRate !== null) {
            const windowSec = toNumberOrNull(payload?.asb_messages_per_second_window_sec);
            if (windowSec !== null) {
              setAsbRateWindowSec(windowSec);
            }
            setLatestAsbMessagesPerSecond(asbRate);
            asbRateHistoryRef.current = [
              ...asbRateHistoryRef.current,
              { observed_at: new Date(), rate: asbRate },
            ].slice(-MAX_HISTORY);
            setAsbRateHistory([...asbRateHistoryRef.current]);
          }
          setError('');
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Unknown error');
        }
      }
    };

    const fetchConfig = async () => {
      try {
        const response = await auth.fetchWithAuth('/api/config');
        if (!response.ok) {
          throw new Error(`Config request failed: ${response.status}`);
        }
        const payload = await response.json();
        if (isMounted) {
          setConfigPayload(payload);
          setConfigError('');
        }
      } catch (err) {
        if (isMounted) {
          setConfigError(err instanceof Error ? err.message : 'Unknown error');
        }
      }
    };

    fetchLiveChart();
    fetchConfig();
    const intervalId = setInterval(() => {
      fetchLiveChart();
      fetchConfig();
    }, 5000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [auth.authChecked, auth.fetchWithAuth, auth.isAuthenticated, lineId]);

  useEffect(() => {
    const clockId = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(clockId);
  }, []);

  const toPlotTime = (value) => {
    if (value == null || value === '') {
      return null;
    }
    if (displayTimezone) {
      return utcInstantToSiteWallClockDate(value, displayTimezone);
    }
    return parseUtcInstant(value);
  };

  const timeLabels = dataHistory.map((d) => toPlotTime(d.setting_time));
  const latestData = dataHistory.length > 0 ? dataHistory[dataHistory.length - 1] : null;
  const isUnitsSchedulerInUse = latestData?.units_scheduler_in_use === true;
  const configLine = configPayload?.line_configs?.find((line) => line?.line_no === lineId) ?? null;
  const configItems = configLine?.items ?? [];
  const lineTimezone = configItems.find((item) => item?.key === 'timezone')?.value ?? null;
  const globalItems = configPayload?.global_items ?? [];

  const newSettingTrace = {
    x: timeLabels,
    y: dataHistory.map((d) => d.new_setting ?? null),
    type: 'scatter',
    mode: 'lines+markers',
    name: 'New Setting',
    line: { color: '#1f77b4', width: 2 },
    marker: { size: 4 },
  };

  const acceptedTrace = {
    x: timeLabels,
    y: dataHistory.map((d) => d.accepted ?? null),
    type: 'scatter',
    mode: 'lines+markers',
    name: 'Accepted',
    line: { color: '#ff7f0e', width: 2 },
    marker: { size: 4 },
  };

  const nominalWeightTrace = {
    x: timeLabels,
    y: dataHistory.map((d) => d.nominal_weight ?? null),
    type: 'scatter',
    mode: 'lines+markers',
    name: 'Nominal Weight',
    line: { color: '#2ca02c', width: 2 },
    marker: { size: 4 },
  };

  const correctionTrace = {
    x: timeLabels,
    y: dataHistory.map((d) => d.correction ?? null),
    type: 'scatter',
    mode: 'lines+markers',
    name: 'Correction',
    line: { color: '#d62728', width: 2 },
    marker: { size: 4 },
  };

  const currentGaTrace = {
    x: timeLabels,
    y: dataHistory.map((d) => d.current_ga ?? null),
    type: 'scatter',
    mode: 'lines+markers',
    name: 'Current GA',
    line: { color: '#9467bd', width: 2 },
    marker: { size: 4 },
  };

  const recentRejectsTrace = {
    x: timeLabels,
    y: dataHistory.map((d) => d.rejections_count ?? null),
    type: 'scatter',
    mode: 'lines+markers',
    name: 'Rejected packs (Status≠0, excl. device no-weight)',
    line: { color: '#8c564b', width: 2 },
    marker: { size: 4 },
  };

  const recentUnderweightTrace = {
    x: timeLabels,
    y: dataHistory.map((d) => d.recent_underweight_count ?? null),
    type: 'scatter',
    mode: 'lines+markers',
    name: 'Underweight (SystemRejectReason)',
    line: { color: '#e377c2', width: 2 },
    marker: { size: 4 },
  };

  const meanSettingDifferenceTrace = {
    x: timeLabels,
    y: dataHistory.map((d) => d.mean_setting_difference ?? null),
    type: 'scatter',
    mode: 'lines+markers',
    name: 'Mean Setting Difference',
    line: { color: '#17becf', width: 2 },
    marker: { size: 4 },
  };

  const currentMuTrace = {
    x: timeLabels,
    y: dataHistory.map((d) => d.current_mu ?? null),
    type: 'scatter',
    mode: 'lines+markers',
    name: 'Current μ (expected mean)',
    line: { color: '#bcbd22', width: 2 },
    marker: { size: 4 },
  };

  const packMeanTimeLabels = dataHistory.map((d) =>
    packMeanPlotTime(
      d.setting_time,
      d.line_delay_seconds,
      d.update_setting_interval_seconds,
      displayTimezone,
    ),
  );

  const packMeanTrace = {
    x: packMeanTimeLabels,
    y: dataHistory.map((d) => d.pack_mean ?? null),
    type: 'scatter',
    mode: 'lines+markers',
    name: 'Observed pack mean (time − max(0, delay−interval))',
    line: { color: '#7f7f7f', width: 2, dash: 'dot' },
    marker: { size: 4 },
  };

  // One marker per distinct vemag creation_date_utc (newest-only on backend); markers avoid line zig-zags.
  const actualSettingPoints = [];
  let lastVemagTimeMs = null;
  for (const d of dataHistory) {
    const x = toPlotTime(d.current_vemag_setting_date);
    const y = d.current_vemag_setting_value ?? null;
    if (x == null || y == null) {
      continue;
    }
    const timeMs = x.getTime();
    if (timeMs === lastVemagTimeMs) {
      continue;
    }
    lastVemagTimeMs = timeMs;
    actualSettingPoints.push({ x, y });
  }

  const actualSettingTrace = {
    x: actualSettingPoints.map((p) => p.x),
    y: actualSettingPoints.map((p) => p.y),
    type: 'scatter',
    mode: 'lines+markers',
    name: 'Actual Setting',
    line: { color: '#ef4444', width: 2 },
    marker: { size: 4 },
    hovertemplate: 'Actual Setting: %{y}<br>Vemag time: %{x|%d/%m/%Y %H:%M:%S}<extra></extra>',
  };

  const minUnitWeightTrace = {
    x: timeLabels,
    y: dataHistory.map((d) => d.min_unit_weight ?? null),
    type: 'scatter',
    mode: 'lines+markers',
    name: 'Min Unit Weight',
    line: { color: '#0ea5e9', width: 2 },
    marker: { size: 4 },
  };

  const maxUnitWeightTrace = {
    x: timeLabels,
    y: dataHistory.map((d) => d.max_unit_weight ?? null),
    type: 'scatter',
    mode: 'lines+markers',
    name: 'Max Unit Weight',
    line: { color: '#f59e0b', width: 2 },
    marker: { size: 4 },
  };

  const asbMessagesPerSecondTrace = {
    x: asbRateHistory.map((d) =>
      d.observed_at instanceof Date
        ? toPlotTime(d.observed_at.toISOString())
        : toPlotTime(d.observed_at),
    ),
    y: asbRateHistory.map((d) => d.rate ?? null),
    type: 'scatter',
    mode: 'lines+markers',
    name: 'ASB messages/s (global)',
    line: { color: '#6366f1', width: 2 },
    marker: { size: 4 },
  };

  const unitsPerPackTrace = {
    x: timeLabels,
    y: dataHistory.map((d) => d.units_per_pack ?? null),
    type: 'scatter',
    mode: 'lines+markers',
    name: 'Units Per Pack',
    line: { color: '#10b981', width: 2 },
    marker: { size: 4 },
  };

  const maxCutToVemagDiffTrace = {
    x: timeLabels,
    y: dataHistory.map((d) => d.max_cut_to_vemag_diff_seconds ?? null),
    type: 'scatter',
    mode: 'lines+markers',
    name: 'Max(CutDate − VemagDate) [s]',
    line: { color: '#dc2626', width: 2 },
    marker: { size: 4 },
  };

  const boolToStatusY = (value) => (value === null || value === undefined ? null : value ? 1 : 0);

  const isRunningStatusTrace = {
    x: timeLabels,
    y: dataHistory.map((d) => boolToStatusY(d.line_heartbeat_is_running)),
    type: 'scatter',
    mode: 'lines+markers',
    name: 'isRunning',
    line: { color: '#16a34a', width: 2, shape: 'hv' },
    marker: { size: 4 },
  };

  const isDowntimeActiveStatusTrace = {
    x: timeLabels,
    y: dataHistory.map((d) => boolToStatusY(d.line_heartbeat_is_downtime_active)),
    type: 'scatter',
    mode: 'lines+markers',
    name: 'isDowntimeActive',
    line: { color: '#dc2626', width: 2, shape: 'hv' },
    marker: { size: 4 },
  };

  const isAiTurnedOnStatusTrace = {
    x: timeLabels,
    y: dataHistory.map((d) => boolToStatusY(d.line_heartbeat_is_ai_turned_on)),
    type: 'scatter',
    mode: 'lines+markers',
    name: 'isAiTurnedOn',
    line: { color: '#2563eb', width: 2, shape: 'hv' },
    marker: { size: 4 },
  };

  const commonLayout = {
    margin: { l: 60, r: 40, t: 30, b: 40 },
    hovermode: 'x unified',
  };
  const timeAxisTitle = displayTimezone ? `Time (${displayTimezone})` : 'Time';

  return (
    <AuthGate auth={auth}>
      <div style={{ padding: '20px', fontFamily: 'sans-serif' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
          <h1 style={{ margin: 0 }}>Live Chart - Line {lineId}</h1>
          {displayTimezone ? (
            <span style={{ color: '#64748b', fontSize: '14px' }}>
              Times shown in {displayTimezone}
              {serverName ? ` (${serverName})` : ''}
            </span>
          ) : null}
          <div style={{ display: 'flex', alignItems: 'center', gap: '18px' }}>
            <StatusLight label="isRunning" ok={latestData?.line_heartbeat_is_running ?? null} />
            <StatusLight
              label="isDowntimeActive"
              ok={
                latestData?.line_heartbeat_is_downtime_active === null ||
                latestData?.line_heartbeat_is_downtime_active === undefined
                  ? null
                  : !latestData.line_heartbeat_is_downtime_active
              }
              value={latestData?.line_heartbeat_is_downtime_active ?? null}
            />
            <StatusLight
              label="isAiTurnedOn"
              ok={latestData?.line_heartbeat_is_ai_turned_on ?? null}
            />
          </div>
        </div>
        {error && <div style={{ color: 'red', marginBottom: '20px' }}>Error: {error}</div>}
        <div style={{ marginBottom: '20px' }}>
          <button
            type="button"
            onClick={() => setShowConfig((prev) => !prev)}
            style={{
              background: showConfig ? '#0f172a' : '#1f2937',
              color: '#fff',
              border: 'none',
              padding: '10px 14px',
              borderRadius: '8px',
              cursor: 'pointer',
            }}
          >
            {showConfig ? 'Hide config parameters' : 'Show config parameters'}
          </button>
          {configError && (
            <div style={{ color: 'red', marginTop: '10px' }}>Config error: {configError}</div>
          )}
        </div>
        {showConfig && (
          <div style={{ marginBottom: '30px' }}>
            <h2>Current config parameters</h2>
            {configPayload ? (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                  gap: '12px',
                }}
              >
                {configItems.map((item) => (
                  <div
                    key={`line-${item.key}`}
                    style={{
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: '10px',
                      padding: '12px 14px',
                    }}
                  >
                    <div style={{ fontSize: '12px', color: '#475569', marginBottom: '4px', textTransform: 'uppercase' }}>
                      {item.key} (line)
                    </div>
                    <div style={{ fontSize: '18px', fontWeight: 600, color: '#0f172a' }}>
                      {toConfigDisplay(item.value)}
                    </div>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>
                      Source: {item.source} {item.editable ? '' : '(read-only)'}
                    </div>
                  </div>
                ))}
                {globalItems.map((item) => (
                  <div
                    key={`global-${item.key}`}
                    style={{
                      background: '#f1f5f9',
                      border: '1px solid #cbd5f5',
                      borderRadius: '10px',
                      padding: '12px 14px',
                    }}
                  >
                    <div style={{ fontSize: '12px', color: '#475569', marginBottom: '4px', textTransform: 'uppercase' }}>
                      {item.key} (global)
                    </div>
                    <div style={{ fontSize: '18px', fontWeight: 600, color: '#0f172a' }}>
                      {toConfigDisplay(item.value)}
                    </div>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>
                      Source: {item.source} {item.editable ? '' : '(read-only)'}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div>Loading config...</div>
            )}
          </div>
        )}
        {dataHistory.length === 0 ? (
          <div>Loading data...</div>
        ) : (
          <div>
            <div style={{ marginBottom: '20px' }}>
              <h2>ASB ingest (global)</h2>
              <p style={{ color: '#64748b', fontSize: '14px', marginTop: '-8px', marginBottom: '12px' }}>
                Średnia liczba wiadomości Service Bus odebranych na sekundę (wszystkie linie i typy), niezależna od
                wybranej linii. Okno uśredniania:{' '}
                <strong>{asbRateWindowSec != null ? `${asbRateWindowSec}s` : '30s'}</strong>.
              </p>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                  gap: '12px',
                  marginBottom: '20px',
                }}
              >
                <div
                  style={{
                    background: '#eef2ff',
                    border: '1px solid #c7d2fe',
                    borderRadius: '10px',
                    padding: '12px 14px',
                  }}
                >
                  <div style={{ fontSize: '12px', color: '#475569', marginBottom: '4px', textTransform: 'uppercase' }}>
                    ASB messages / s
                  </div>
                  <div style={{ fontSize: '22px', fontWeight: 700, color: '#312e81' }}>
                    {latestAsbMessagesPerSecond != null
                      ? latestAsbMessagesPerSecond.toFixed(2)
                      : 'N/A'}
                  </div>
                </div>
                <div
                  style={{
                    background: '#eef2ff',
                    border: '1px solid #c7d2fe',
                    borderRadius: '10px',
                    padding: '12px 14px',
                  }}
                >
                  <div style={{ fontSize: '12px', color: '#475569', marginBottom: '4px', textTransform: 'uppercase' }}>
                    Current time{lineTimezone ? ` (${lineTimezone})` : ''}
                  </div>
                  <div style={{ fontSize: '22px', fontWeight: 700, color: '#312e81' }}>
                    {formatSiteDateTime(now, lineTimezone) || 'N/A'}
                  </div>
                </div>
              </div>
              {asbRateHistory.length > 1 ? (
                <Plot
                  data={[asbMessagesPerSecondTrace]}
                  layout={{
                    ...commonLayout,
                    title: 'ASB inbound rate (messages/s)',
                    yaxis: { title: 'Messages / s' },
                    xaxis: { title: displayTimezone ? `Observed at (${displayTimezone})` : 'Observed at (client poll time)' },
                  }}
                  style={{ width: '100%', height: '320px' }}
                  config={{ responsive: true }}
                />
              ) : (
                <div style={{ color: '#64748b', fontSize: '14px' }}>Collecting ASB rate samples…</div>
              )}
            </div>

            <div style={{ marginBottom: '20px' }}>
              <h2>Order details</h2>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                  gap: '12px',
                }}
              >
                <DetailCard label="Order ID" value={toDisplayValue(latestData?.order_id)} />
                <DetailCard label="Order Length" value={toDisplayValue(latestData?.order_length)} />
                <DetailCard
                  label="Last pack time"
                  value={latestData?.last_package_at ? formatSiteDateTime(latestData.last_package_at, displayTimezone) : 'N/A'}
                />
                <DetailCard
                  label="Last vemag setting time"
                  value={
                    latestData?.current_vemag_setting_date
                      ? formatSiteDateTime(latestData.current_vemag_setting_date, displayTimezone)
                      : 'N/A'
                  }
                />
                <DetailCard
                  label="Current GA"
                  value={typeof latestData?.current_ga === 'number' ? `${latestData.current_ga.toFixed(1)} g` : 'N/A'}
                />
                <DetailCard
                  label="Expected mean (Mu)"
                  value={typeof latestData?.current_mu === 'number' ? `${latestData.current_mu.toFixed(1)} g` : 'N/A'}
                />
                <DetailCard
                  label="Last pack weight"
                  value={
                    typeof latestData?.last_package_weight_g === 'number' &&
                    !Number.isNaN(latestData.last_package_weight_g)
                      ? `${latestData.last_package_weight_g.toFixed(1)} g`
                      : 'N/A'
                  }
                />
                <DetailCard
                  label="last Vemag setting"
                  value={
                    vemagValuePresent(latestData?.current_vemag_setting_value)
                      ? `${parseFloat(latestData.current_vemag_setting_value).toFixed(1)} g`
                      : 'N/A'
                  }
                />
                {isUnitsSchedulerInUse ? (
                  <>
                    <DetailCard label="Units per pack" value={toDisplayValue(latestData?.units_per_pack)} />
                    <DetailCard
                      label="Min unit weight"
                      value={latestData?.min_unit_weight != null ? `${latestData.min_unit_weight} g` : 'N/A'}
                    />
                    <DetailCard
                      label="Max unit weight"
                      value={latestData?.max_unit_weight != null ? `${latestData.max_unit_weight} g` : 'N/A'}
                    />
                  </>
                ) : null}
              </div>
            </div>

            <div style={{ marginBottom: '30px' }}>
              <h2>Setting prediction and Actual setting</h2>
              <Plot
                data={[newSettingTrace, actualSettingTrace]}
                layout={{
                  ...commonLayout,
                  title: 'Setting prediction and Actual setting',
                  yaxis: { title: 'Value' },
                  xaxis: { title: timeAxisTitle },
                }}
                style={{ width: '100%', height: '400px' }}
                config={{ responsive: true }}
              />
            </div>

            <div style={{ marginBottom: '30px' }}>
              <h2>Accepted</h2>
              <Plot
                data={[acceptedTrace]}
                layout={{
                  ...commonLayout,
                  title: 'Accepted',
                  yaxis: { title: 'Count' },
                  xaxis: { title: timeAxisTitle },
                }}
                style={{ width: '100%', height: '400px' }}
                config={{ responsive: true }}
              />
            </div>

            <div style={{ marginBottom: '30px' }}>
              <h2>Nominal Weight</h2>
              <Plot
                data={[nominalWeightTrace]}
                layout={{
                  ...commonLayout,
                  title: 'Nominal Weight',
                  yaxis: { title: 'Weight' },
                  xaxis: { title: timeAxisTitle },
                }}
                style={{ width: '100%', height: '400px' }}
                config={{ responsive: true }}
              />
            </div>

            <div style={{ marginBottom: '30px' }}>
              <h2>Setting change</h2>
              <Plot
                data={[correctionTrace]}
                layout={{
                  ...commonLayout,
                  title: 'Correction',
                  yaxis: { title: 'Correction' },
                  xaxis: { title: timeAxisTitle },
                }}
                style={{ width: '100%', height: '400px' }}
                config={{ responsive: true }}
              />
            </div>

            <div style={{ marginBottom: '30px' }}>
              <h2>Current GA</h2>
              <Plot
                data={[currentGaTrace]}
                layout={{
                  ...commonLayout,
                  title: 'Current GA',
                  yaxis: { title: 'Current GA' },
                  xaxis: { title: timeAxisTitle },
                }}
                style={{ width: '100%', height: '400px' }}
                config={{ responsive: true }}
              />
            </div>

            <div style={{ marginBottom: '30px' }}>
              <h2>Rejects &amp; underweights</h2>
              <p style={{ color: '#64748b', fontSize: '14px', marginTop: '-8px', marginBottom: '12px' }}>
                Rolling counts in the last <strong>delay</strong> seconds for the current order (scheduler GA window).{' '}
                <em>Rejected packs</em>: Status≠0, excluding DeviceRejectReason=RejectNoWeight.{' '}
                <em>Underweight</em>: SystemRejectReason=Underweight only.
              </p>
              <Plot
                data={[recentRejectsTrace, recentUnderweightTrace]}
                layout={{
                  ...commonLayout,
                  title: 'Rejects vs underweights (recent window)',
                  yaxis: { title: 'Count' },
                  xaxis: { title: timeAxisTitle },
                }}
                style={{ width: '100%', height: '400px' }}
                config={{ responsive: true }}
              />
            </div>

            <div style={{ marginBottom: '30px' }}>
              <h2>Mean Setting Difference</h2>
              <Plot
                data={[meanSettingDifferenceTrace]}
                layout={{
                  ...commonLayout,
                  title: 'Mean Setting Difference',
                  yaxis: { title: 'Value' },
                  xaxis: { title: timeAxisTitle },
                }}
                style={{ width: '100%', height: '400px' }}
                config={{ responsive: true }}
              />
            </div>

            <div style={{ marginBottom: '30px' }}>
              <h2>Current μ vs observed pack mean</h2>
              <p style={{ color: '#64748b', fontSize: '14px', marginTop: '-8px', marginBottom: '12px' }}>
                <strong>Current μ</strong> — czas osi = <code>setting_time</code> (jak w zapisie do DB).{' '}
                <strong>Observed pack mean</strong> — średnia wag z checkweighera z tego samego zestawu paczek co μ w
                schedulerze; na osi czasu punkt jest przesunięty w lewo o{' '}
                <code>max(0, delay − UPDATE_SETTING_INTERVAL)</code> sekund (jak początek okna{' '}
                <code>window_start</code> przy porównaniu shifted μ w wykresie zlecenia). Brakujące{' '}
                <code>line_delay_seconds</code> / <code>update_setting_interval_seconds</code> w payloadzie traktujemy
                jak 0. To nie jest kolejka <code>recent_means</code> z GA.
              </p>
              <Plot
                data={[currentMuTrace, packMeanTrace]}
                layout={{
                  ...commonLayout,
                  title: 'Current μ vs observed pack mean',
                  yaxis: { title: 'Weight (g)' },
                  xaxis: { title: timeAxisTitle },
                }}
                style={{ width: '100%', height: '400px' }}
                config={{ responsive: true }}
              />
            </div>

            <div style={{ marginBottom: '30px' }}>
              <h2>Max pack/vemag time skew</h2>
              <p style={{ color: '#64748b', fontSize: '14px', marginTop: '-8px', marginBottom: '12px' }}>
                Dla każdej paczki dopasowanej w tym ticku liczymy{' '}
                <code>CutDate − vemag.creation_date_utc</code> (gdzie{' '}
                <code>CutDate = RealProductionDate − line_delay</code>, a vemag to ostatnie ustawienie nie później niż{' '}
                <code>CutDate</code>). Na wykresie pokazujemy <strong>max</strong> tej różnicy w sekundach po wszystkich
                paczkach z ticka. Wysokie wartości = vemag dopasowany do paczki jest stary względem jej cięcia, co bywa
                źródłem złych dopasowań paczka↔ustawienie. <code>null</code> w ticku oznacza brak paczek z vemag-em
                (np. brak danych vemag w oknie).
              </p>
              <Plot
                data={[maxCutToVemagDiffTrace]}
                layout={{
                  ...commonLayout,
                  title: 'Max(CutDate − VemagDate) per tick',
                  yaxis: { title: 'Seconds' },
                  xaxis: { title: timeAxisTitle },
                }}
                style={{ width: '100%', height: '400px' }}
                config={{ responsive: true }}
              />
            </div>

            {isUnitsSchedulerInUse && (
              <>
                <div style={{ marginBottom: '30px' }}>
                  <h2>Unit Weights</h2>
                  <Plot
                    data={[minUnitWeightTrace, maxUnitWeightTrace]}
                    layout={{
                      ...commonLayout,
                      title: 'Unit Weights',
                      yaxis: { title: 'Weight' },
                      xaxis: { title: timeAxisTitle },
                    }}
                    style={{ width: '100%', height: '400px' }}
                    config={{ responsive: true }}
                  />
                </div>

                <div style={{ marginBottom: '30px' }}>
                  <h2>Units Per Pack</h2>
                  <Plot
                    data={[unitsPerPackTrace]}
                    layout={{
                      ...commonLayout,
                      title: 'Units Per Pack',
                      yaxis: { title: 'Count' },
                      xaxis: { title: timeAxisTitle },
                    }}
                    style={{ width: '100%', height: '400px' }}
                    config={{ responsive: true }}
                  />
                </div>
              </>
            )}

            <div style={{ marginBottom: '30px' }}>
              <h2>Line status (heartbeat)</h2>
              <p style={{ color: '#64748b', fontSize: '14px', marginTop: '-8px', marginBottom: '12px' }}>
                Status linii z heartbeatu ASB: <strong>1 = true</strong>, <strong>0 = false</strong> (puste = brak
                danych). <code>isRunning</code> — czy linia działa; <code>isDowntimeActive</code> — czy linia jest
                zepsuta; <code>isAiTurnedOn</code> — czy AI jest włączone (null = brak info).
              </p>
              <Plot
                data={[isRunningStatusTrace, isDowntimeActiveStatusTrace, isAiTurnedOnStatusTrace]}
                layout={{
                  ...commonLayout,
                  title: 'Line status over time',
                  yaxis: {
                    title: 'Status',
                    range: [-0.1, 1.1],
                    tickvals: [0, 1],
                    ticktext: ['false (0)', 'true (1)'],
                  },
                  xaxis: { title: timeAxisTitle },
                }}
                style={{ width: '100%', height: '400px' }}
                config={{ responsive: true }}
              />
            </div>
          </div>
        )}
      </div>
    </AuthGate>
  );
}

export default LiveChartPage;